import type { CurvaDocument } from "@/document/model/document";
import { getSceneNodeChildren } from "@/document/model/document";
import type { FrameNode, SceneNode } from "@/document/model/nodes";
import type { Rect } from "@/lib/math/rect";

export interface BoundsFrameContext {
  frameId: string;
  frameBounds: Rect;
}

export function sharedNearestParentFrameContext(document: CurvaDocument, nodeIds: string[]): BoundsFrameContext | undefined {
  if (nodeIds.length === 0) {
    return undefined;
  }

  const contexts = nodeIds.map(nodeId => parentFrameContextForNode(document, nodeId));
  const first = contexts[0];
  if (!first || contexts.some(context => context?.frameId !== first.frameId)) {
    return undefined;
  }

  return first;
}

export function parentFrameContextForNode(document: CurvaDocument, nodeId: string): BoundsFrameContext | undefined {
  for (const page of document.pages) {
    const context = nearestParentFrameContextInNodes(page.children, nodeId);
    if (context) {
      return context;
    }
  }

  return undefined;
}

export function inspectorBoundsFromDocumentBounds(bounds: Rect, context: BoundsFrameContext | undefined): Rect {
  if (!context) {
    return bounds;
  }

  return {
    ...bounds,
    x: bounds.x - context.frameBounds.x,
    y: bounds.y - context.frameBounds.y,
  };
}

export function documentBoundsFromInspectorBounds(bounds: Rect, context: BoundsFrameContext | undefined): Rect {
  if (!context) {
    return bounds;
  }

  return {
    ...bounds,
    x: bounds.x + context.frameBounds.x,
    y: bounds.y + context.frameBounds.y,
  };
}

function nearestParentFrameContext(document: CurvaDocument, nodeId: string): BoundsFrameContext | undefined {
  return parentFrameContextForNode(document, nodeId);
}

function nearestParentFrameContextInNodes(
  nodes: SceneNode[],
  nodeId: string,
  parentFrame?: FrameNode,
): BoundsFrameContext | undefined {
  for (const node of nodes) {
    if (node.id === nodeId) {
      return parentFrame ? { frameId: parentFrame.id, frameBounds: parentFrame.bounds } : undefined;
    }

    const children = getSceneNodeChildren(node);
    if (!children) {
      continue;
    }

    const context = nearestParentFrameContextInNodes(
      children,
      nodeId,
      node.type === "frame" ? node : parentFrame,
    );
    if (context) {
      return context;
    }
  }

  return undefined;
}
